import { useState, useRef } from 'react';
import { X, Download, Upload, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useImportCatalogCsv } from '@/hooks/useCatalog';

interface Props {
  onClose: () => void;
}

const CSV_TEMPLATE = `name,category,price,buy_url,brand,sku,part_number,delivery_days_min,delivery_days_max
Pneu Michelin 205/55 R16,Pneus,459.90,https://loja.com/pneu-michelin,Michelin,MICH-205,205/55R16,3,7
Pastilha de freio Bosch,Freios,189.90,https://loja.com/pastilha-bosch,Bosch,BOSCH-PF1,0986BB0618,1,3
Oleo Mobil 5W30 1L,Lubrificantes,42.90,https://loja.com/mobil-5w30,Mobil,MOB-5W30,,2,5`;

export function CatalogCsvImportModal({ onClose }: Props) {
  const { user } = useAuth();
  const pid = user?.partnerId ?? '';
  const importMutation = useImportCatalogCsv(pid);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewLines, setPreviewLines] = useState<string[]>([]);

  function handleDownloadTemplate() {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template-catalogo.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleFileSelect(f: File) {
    setFile(f);
    const text = await f.text();
    const lines = text.split('\n').filter((l) => l.trim());
    setPreviewLines(lines.slice(0, 6)); // header + 5 lines
  }

  function handleImport() {
    if (!file) return;
    importMutation.mutate(file);
  }

  function handleDownloadErrors() {
    if (!importMutation.data?.errors.length) return;
    const header = 'row,field,message';
    const lines = importMutation.data.errors.map((e) => `${e.row},${e.field},"${e.message}"`);
    const csv = [header, ...lines].join('\n');
    // BOM + charset: as mensagens vem da API em portugues ("Preço inválido: …")
    // e sem isto o Excel as abre como mojibake — justamente o arquivo que o
    // parceiro baixa PARA conseguir corrigir a planilha.
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'erros-importacao.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  const result = importMutation.data;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

      <div className="relative z-10 w-full max-w-lg rounded-xl bg-background p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Importar produtos via CSV</h2>
          <button aria-label="Fechar" onClick={onClose} className="rounded p-1 hover:bg-muted"><X size={18} /></button>
        </div>

        <div className="mt-4 space-y-4">
          {/* Step 1: Template */}
          <div>
            <p className="text-sm text-muted-foreground">1. Baixe o template com as colunas do catálogo</p>
            <button onClick={handleDownloadTemplate}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted">
              <Download size={14} /> Baixar template
            </button>
          </div>

          {/* Step 2: Upload */}
          <div>
            <p className="text-sm text-muted-foreground">2. Preencha e envie o arquivo</p>
            <div onClick={() => fileInputRef.current?.click()}
              className="mt-2 flex cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-border py-6 hover:bg-muted">
              {file ? (
                <span className="text-sm font-medium">{file.name}</span>
              ) : (
                <div className="flex flex-col items-center gap-1 text-muted-foreground">
                  <Upload size={20} />
                  <span className="text-xs">Clique para selecionar o CSV</span>
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }} />
          </div>

          {/* Preview */}
          {previewLines.length > 0 && !result && (
            <div className="rounded-lg bg-muted p-3">
              <div className="text-xs font-medium text-muted-foreground mb-1">Preview ({previewLines.length - 1} linhas)</div>
              <div className="overflow-x-auto">
                <table className="text-xs">
                  <thead>
                    <tr>
                      {previewLines[0].split(',').map((h, i) => (
                        <th key={i} className="px-2 py-1 text-left font-medium">{h.trim()}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewLines.slice(1).map((line, i) => (
                      <tr key={i}>
                        {line.split(',').map((v, j) => (
                          <td key={j} className="px-2 py-0.5 text-muted-foreground">{v.trim()}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-2 rounded-lg bg-muted p-3">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle size={16} className="text-green-600 dark:text-green-400" />
                <span className="font-medium">{result.imported} produtos importados</span>
              </div>
              {result.errors.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-destructive">
                    {result.errors.length} erros encontrados:
                  </div>
                  {result.errors.slice(0, 10).map((err, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-destructive">
                      <AlertCircle size={12} />
                      Linha {err.row} ({err.field}): {err.message}
                    </div>
                  ))}
                  {result.errors.length > 10 && (
                    <p className="text-xs text-muted-foreground">
                      ... e mais {result.errors.length - 10} erros
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            {result?.errors && result.errors.length > 0 && (
              <button onClick={handleDownloadErrors}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted">
                <Download size={14} /> Baixar erros
              </button>
            )}
            <button onClick={onClose}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted">
              {result ? 'Fechar' : 'Cancelar'}
            </button>
            {!result && (
              <button onClick={handleImport} disabled={!file || importMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
                {importMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                Importar válidos
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
